import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import CatalogueAutocomplete from '../doctor/CatalogueAutocomplete.jsx'
import { searchDrugs } from '../../api/catalogues.js'
import { createStockRow } from '../../api/pharmacyStock.js'

/**
 * Flatten a DRF error payload into a readable one-line message.
 *
 * DRF returns errors in several shapes depending on where the failure
 * originated:
 *   * Validation:  `{fieldname: ["msg1", "msg2"], other: ["msg"]}`
 *   * Non-field:   `{non_field_errors: ["The fields X, Y must make a unique set."]}`
 *   * Detail:      `{detail: "You do not have permission…"}`
 *   * Nested:      `{errors: {nested: {deeper: ["msg"]}}}`
 *
 * Recursive walker so we never end up template-string-coercing an object
 * to `[object Object]`. Depth cap keeps a malformed response from looping.
 */
function formatDrfError(node, depth = 0) {
  if (depth > 5) return ''
  if (node == null) return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number' || typeof node === 'boolean') return String(node)
  if (Array.isArray(node)) {
    return node
      .map((item) => formatDrfError(item, depth + 1))
      .filter(Boolean)
      .join(', ')
  }
  if (typeof node === 'object') {
    // Special-case DRF's `{detail: "..."}` — just return the message.
    if (Object.keys(node).length === 1 && typeof node.detail === 'string') {
      return node.detail
    }
    return Object.entries(node)
      .map(([key, val]) => {
        const inner = formatDrfError(val, depth + 1)
        if (!inner) return ''
        // Suppress the key for DRF's synthetic `non_field_errors` bucket
        // since it's noise to end users.
        if (key === 'non_field_errors' || key === 'detail') return inner
        return `${key}: ${inner}`
      })
      .filter(Boolean)
      .join('; ')
  }
  return ''
}

/**
 * AddDrugModal — one-drug-at-a-time catalogue entry for a pharmacy admin.
 *
 * Two-step flow inside the modal:
 *   1. Pick a drug from the global formulary (~1820 rows, searchable via
 *      the existing CatalogueAutocomplete + searchDrugs).
 *   2. Enter this pharmacy's specifics: brand name, price (PGK),
 *      stock quantity, reorder level, availability. Submit.
 *
 * The server (`PharmacyStockViewSet.perform_create`) forces the pharmacy
 * FK to the caller's active pharmacy for non-admins, so we don't need to
 * pass a pharmacy_id from the FE for the pharmacy-staff case.
 *
 * Deliberately does NOT include image upload — the current image model
 * is global (`Drug.image`, admin-only). Uploading an image is a separate
 * platform-admin action via the existing per-row edit affordance in
 * PharmacyCatalogue.jsx. If pharmacies later ask for per-stock overrides,
 * that becomes a schema change (PharmacyStock.image) and a follow-up PR.
 *
 * Duplicate handling: UniqueConstraint(pharmacy, drug) at the DB layer
 * means a POST with the same (pharmacy, drug) fails with a 400. The
 * mutation's onError surfaces a friendly "You already stock this drug"
 * so the operator can go edit the existing row inline instead.
 */
export default function AddDrugModal({ isOpen, onClose, onCreated }) {
  const qc = useQueryClient()
  const [selectedDrug, setSelectedDrug] = useState(null)
  const [brandName, setBrandName] = useState('')
  const [price, setPrice] = useState('')
  const [stockQuantity, setStockQuantity] = useState('0')
  const [reorderLevel, setReorderLevel] = useState('0')
  const [isAvailable, setIsAvailable] = useState(true)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')

  const createMut = useMutation({
    mutationFn: (payload) => createStockRow(payload),
    onSuccess: (row) => {
      // Refresh the parent catalogue's list + low-stock banner so the
      // new row appears immediately. Query keys match those in
      // PharmacyCatalogue.jsx :: StockListCard (hyphenated).
      qc.invalidateQueries({ queryKey: ['pharmacy-stock'] })
      qc.invalidateQueries({ queryKey: ['pharmacy-stock-summary'] })
      onCreated?.(row)
      resetAndClose()
    },
    onError: (err) => {
      const detail = err?.response?.data
      // Recursively flatten DRF's error dict / list shapes into a readable
      // one-line message. Handles: strings, arrays, nested {field: [msgs]}
      // dicts, and DRF's `{detail: '...'}` wrapper. Falls back to the raw
      // JSON as a last resort so a shape we haven't seen still surfaces
      // something diagnosable rather than "[object Object]".
      const asString = formatDrfError(detail)
      const lower = asString.toLowerCase()
      if (lower.includes('unique') || lower.includes('already') || lower.includes('must make a unique set')) {
        setError('You already stock this drug — go to Section 3 and edit the existing row instead.')
        return
      }
      setError(asString || 'Could not add drug. Please check your entries and try again.')
    },
  })

  function resetAndClose() {
    setSelectedDrug(null)
    setBrandName('')
    setPrice('')
    setStockQuantity('0')
    setReorderLevel('0')
    setIsAvailable(true)
    setNotes('')
    setError('')
    onClose?.()
  }

  function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!selectedDrug?.id) {
      setError('Pick a drug from the search first.')
      return
    }
    const priceNum = price === '' ? null : Number(price)
    if (price !== '' && (Number.isNaN(priceNum) || priceNum < 0)) {
      setError('Price must be a non-negative number (PGK).')
      return
    }
    const stockNum = Number(stockQuantity || 0)
    const reorderNum = Number(reorderLevel || 0)
    if (Number.isNaN(stockNum) || stockNum < 0 || !Number.isInteger(stockNum)) {
      setError('Stock quantity must be a non-negative whole number.')
      return
    }
    if (Number.isNaN(reorderNum) || reorderNum < 0 || !Number.isInteger(reorderNum)) {
      setError('Reorder level must be a non-negative whole number.')
      return
    }
    // Server forces `pharmacy` to the caller's active pharmacy for
    // pharmacy staff (perform_create). Admin path can pass a pharmacy_id
    // via a future prop if needed — deliberately not supported here yet.
    createMut.mutate({
      drug: selectedDrug.id,
      brand_name: brandName.trim(),
      price: priceNum,
      stock_quantity: stockNum,
      reorder_level: reorderNum,
      is_available: isAvailable,
      notes: notes.trim(),
    })
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-drug-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-100 shrink-0">
          <h2 id="add-drug-title" className="text-lg font-semibold text-slate-900">
            Add drug to catalogue
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Pick from the global formulary, then set your price + stock.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto min-h-0">
          <div className="p-4 space-y-4">
            {/* Step 1 — Search + pick a global drug */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                1. Drug (search the global formulary)
              </label>
              {selectedDrug ? (
                <div className="border border-emerald-200 bg-emerald-50 rounded px-3 py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {selectedDrug.name || selectedDrug.medication_name}
                    </div>
                    <div className="text-xs text-slate-600">
                      {[selectedDrug.strength, selectedDrug.form].filter(Boolean).join(' · ')}
                      {selectedDrug.atc_code ? ` · ${selectedDrug.atc_code}` : ''}
                      {selectedDrug.is_otc ? ' · OTC' : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedDrug(null)}
                    className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <CatalogueAutocomplete
                  searchFn={searchDrugs}
                  queryKey="drug-formulary"
                  placeholder="Type at least 2 characters (e.g. amoxi, paracet, metfor)"
                  onPick={(drug) => setSelectedDrug(drug)}
                  renderRow={(drug) => (
                    <div className="min-w-0">
                      <div className="text-sm">
                        {drug.name || drug.medication_name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {[drug.strength, drug.form].filter(Boolean).join(' · ')}
                        {drug.atc_code ? ` · ${drug.atc_code}` : ''}
                        {drug.is_otc ? ' · OTC' : ''}
                      </div>
                    </div>
                  )}
                  rowKey={(drug) => drug.id}
                />
              )}
            </div>

            {/* Step 2 — Pharmacy specifics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  2. Brand name (blank = generic)
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Amoxil, Voltaren"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Price (PGK)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 2.50"
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Stock on hand
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Reorder level
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={reorderLevel}
                  onChange={(e) => setReorderLevel(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
              </div>

              <div className="flex items-center pt-6">
                <input
                  id="is-available-toggle"
                  type="checkbox"
                  checked={isAvailable}
                  onChange={(e) => setIsAvailable(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-wantok-700 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
                <label htmlFor="is-available-toggle" className="ml-2 text-sm text-slate-700">
                  Available to dispense
                </label>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Notes (optional, 255 char)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. new supplier, promo price until end of month"
                  maxLength={255}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-wantok-500"
                  disabled={!selectedDrug || createMut.isPending}
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                {error}
              </div>
            )}
          </div>

          <div className="p-3 sm:p-4 flex justify-end gap-2 border-t border-slate-100 bg-white shrink-0">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={createMut.isPending}
              className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedDrug || createMut.isPending}
              className="text-sm px-4 py-1.5 rounded bg-wantok-700 hover:bg-wantok-800 disabled:opacity-50 text-white font-medium"
            >
              {createMut.isPending ? 'Saving…' : 'Add to catalogue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
